import { createClient } from '@supabase/supabase-js';
import { buildDictionary, buildMultiDict, setSharedRuntime } from '@chinese-reader/shared';

// The Console's Supabase client. Same project as the mobile app, different storage: a browser
// session in localStorage rather than AsyncStorage, and detectSessionInUrl on because the magic
// link returns the session in the URL — that is the whole mechanism of signing in here.
const url = import.meta.env.VITE_SUPABASE_URL;
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!url || !publishableKey) {
  // Fail at startup with the cause named. Vite inlines these at build time, so an empty value is a
  // missing .env — not something a user can fix by retrying, and not worth rediscovering later as
  // a confusing network error.
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY — copy .env.example to .env.',
  );
}

export const supabase = createClient(url, publishableKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// Hand the shared package its client (ADR 0036). No dictionary provider yet: CC-CEDICT arrives
// with the phrase grid, where it will be fetched and cached in IndexedDB rather than bundled.
const dictionaryProvider = (() => {
  let content: Promise<string> | null = null;
  const load = () => {
    if (!content) content = readCachedDictionary();
    return content;
  };
  return {
    getDictionary: async () => buildDictionary(await load()),
    getMultiDictionary: async () => buildMultiDict(await load()),
  };
})();

async function readCachedDictionary(): Promise<string> {
  const database = await new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('chinese-reader-console', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('dictionary');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  const cached = await new Promise<string | undefined>((resolve, reject) => {
    const request = database.transaction('dictionary').objectStore('dictionary').get('cedict');
    request.onsuccess = () => resolve(request.result as string | undefined);
    request.onerror = () => reject(request.error);
  });
  if (cached) return cached;
  const response = await fetch('/cedict_ts.u8');
  if (!response.ok) throw new Error(`CEDICT load failed: ${response.status}`);
  const text = await response.text();
  await new Promise<void>((resolve, reject) => {
    const request = database.transaction('dictionary', 'readwrite').objectStore('dictionary')
      .put(text, 'cedict');
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
  return text;
}

setSharedRuntime({ client: supabase, dictionary: dictionaryProvider });
